import {
    Add,
    Box,
    Button,
    Checkbox,
    Checkmark,
    Close,
    Delete,
    Drag,
    Edit,
    IconButton,
    Modal,
    SearchSelect,
    Select,
    showToast,
    StaticTable,
    SubPageLayout,
    TextInput,
} from "@bcc-ui/ui-components";
import * as React from "react";
import { useTranslation } from "react-i18next";

import style from "./conditional-formatting-rules.scss";
import ApplicationLayout from "components/layout/ApplicationLayout";
import { RULES_ROUTE } from "components/router/Routes";
import { ALL_COLUMNS } from "domain/asset-management/assetPathMapping";
import { AUTH_RENDER_RULE_MANAGE } from "domain/authority";
import {
    apiKeyToColorHex,
    apiToCondition,
    COLOR_OPTIONS,
    colorHexToApiKey,
    CONDITION_OPTIONS,
    ConditionalRuleData,
    ConditionalRuleSet,
    conditionToApi,
    getColorHexFromTheme,
} from "domain/conditionalFormattingRules";
import { assetService } from "services/asset-management/AssetService";
import { conditionalRulesService } from "services/conditional-rules/ConditionalRulesService";
import { createTranslatorPath } from "services/pathTranslator";
import { userSessionService } from "services/user/UserSessionService";
import { resolveColor } from "utils/commonFunctions";
import { useTheme } from "utils/useTheme";

import testIds from "testIds.json";

interface RuleRow {
    id: number;
    enabled: boolean;
    columnName: string;
    condition: string;
    value: string;
    color: string;
    isEditing: boolean;
    order?: number;
    priority?: number;
}

function rulesToApiPayload(rows: RuleRow[]): ConditionalRuleSet {
    const ruleSet: ConditionalRuleSet["ruleSet"] = {};
    // Priority will be used later to implement this rules on actual data
    const priorityCounters: Record<string, number> = {};
    for (let i = 0; i < rows.length; i++) {
        const row = rows[i];
        if (!ruleSet[row.columnName]) {
            ruleSet[row.columnName] = [];
            priorityCounters[row.columnName] = 0;
        }
        let priority: number | undefined;
        if (row.enabled) {
            priority = ++priorityCounters[row.columnName];
        }
        ruleSet[row.columnName].push({
            color: colorHexToApiKey(row.color),
            value: row.value,
            enabled: row.enabled,
            operator: conditionToApi(row.condition),
            order: i,
            priority,
        });
    }
    return { ruleSet };
}

function apiResponseToRules(response: ConditionalRuleData[]): RuleRow[] {
    const rows: RuleRow[] = [];
    let id = 1;
    for (const item of response) {
        for (const [columnName, rules] of Object.entries(item.ruleSet)) {
            for (const rule of rules) {
                rows.push({
                    id: id++,
                    enabled: rule.enabled,
                    columnName,
                    condition: apiToCondition(rule.operator),
                    value: rule.value,
                    color: apiKeyToColorHex(rule.color),
                    isEditing: false,
                    order: rule.order,
                    priority: rule.priority,
                });
            }
        }
    }
    rows.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    return rows;
}

const MAX_RULES = 20;
const MAX_RULES_PER_COLUMN = 10;

function ConditionalFormattingRulesContent(): JSX.Element {
    const { t } = useTranslation();
    const theme = useTheme();
    const translatedPaths = createTranslatorPath(useTranslation, "assetPaths");
    const hasManageRulesAuthority = userSessionService.userHasAllAuthorities([AUTH_RENDER_RULE_MANAGE]);
    const [rules, setRules] = React.useState<RuleRow[]>([]);
    const [savedRules, setSavedRules] = React.useState<RuleRow[]>([]);
    const nextIdRef = React.useRef(1);
    const [availableColumns, setAvailableColumns] = React.useState<{ value: string; label: string }[]>([]);
    const [isCancelDialogVisible, setIsCancelDialogVisible] = React.useState(false);
    const [isSaving, setIsSaving] = React.useState(false);
    const [ruleUuid, setRuleUuid] = React.useState<string | null>(null);
    const [isLoading, setIsLoading] = React.useState(true);
    const [draggedIndex, setDraggedIndex] = React.useState<number | null>(null);
    const editSnapshotRef = React.useRef<Record<number, RuleRow>>({});

    const tableContainerRef = React.useRef(null);

    const handleDragOver = (index: number, event: React.DragEvent) => {
        event.preventDefault();
        if (draggedIndex !== null && draggedIndex !== index) {
            setRules((prev) => {
                const updated = [...prev];
                const [dragged] = updated.splice(draggedIndex, 1);
                updated.splice(index, 0, dragged);
                return updated;
            });
            // track new position after reorder
            setDraggedIndex(index);
        }
    };

    const resetDragState = () => {
        setDraggedIndex(null);
    };

    const allRulesComplete =
        rules.length > 0 && rules.every((r) => !r.isEditing && r.columnName.trim() !== "" && r.value.trim() !== "");
    const allDeletedInEditMode = rules.length === 0 && savedRules.length > 0 && ruleUuid !== null;
    const hasChanges =
        (allRulesComplete || allDeletedInEditMode) && JSON.stringify(rules) !== JSON.stringify(savedRules);

    const columnUsageCount = rules.reduce<Record<string, number>>((acc, r) => {
        if (r.columnName) {
            acc[r.columnName] = (acc[r.columnName] || 0) + 1;
        }
        return acc;
    }, {});

    const getAvailableColumnsForRule = (currentColumnName: string) =>
        availableColumns.filter(
            (col) => col.value === currentColumnName || (columnUsageCount[col.value] || 0) < MAX_RULES_PER_COLUMN
        );

    const handleSave = () => {
        setIsSaving(true);
        const isUpdate = !!ruleUuid;
        const rulesAtSaveTime = [...rules];

        if (ruleUuid && rulesAtSaveTime.length === 0) {
            conditionalRulesService
                .deleteRule(ruleUuid)
                .then(() => {
                    setRuleUuid(null);
                    setSavedRules([]);
                    showToast(t("ConditionalFormattingRules.toast.deleteSuccess"), "success");
                })
                .catch(() => {
                    console.error("Failed to delete conditional formatting rules.");
                })
                .finally(() => setIsSaving(false));
            return;
        }

        const payload = rulesToApiPayload(rulesAtSaveTime);
        const savePromise = ruleUuid
            ? conditionalRulesService.updateRule(ruleUuid, payload)
            : conditionalRulesService.createRule(payload).then((uuid) => {
                  setRuleUuid(uuid);
              });
        savePromise
            .then(() => {
                setSavedRules(rulesAtSaveTime);
                showToast(
                    isUpdate
                        ? t("ConditionalFormattingRules.toast.updateSuccess")
                        : t("ConditionalFormattingRules.toast.saveSuccess"),
                    "success"
                );
            })
            .catch(() => {
                console.error("Failed to save conditional formatting rules.");
            })
            .finally(() => setIsSaving(false));
    };

    const handleCancel = () => {
        if (hasChanges) {
            setIsCancelDialogVisible(true);
        }
    };

    const handleDontSave = () => {
        if (ruleUuid) {
            setRules([...savedRules]);
        } else {
            setRules([]);
            setSavedRules([]);
        }
        setIsCancelDialogVisible(false);
    };

    React.useEffect(() => {
        const abortController = new AbortController();
        const allColumnOptions = ALL_COLUMNS.map((col) => ({
            value: col.value,
            label: translatedPaths(col.value) || col.label,
        }));
        const columnsPromise = assetService
            .fetchExistingKeys(abortController)
            .then((response) => {
                const apiColumns = response.keys.map((key) => ({ value: key, label: translatedPaths(key) }));
                const allValues = new Set<string>(allColumnOptions.map((c) => c.value));
                const extraFromApi = apiColumns.filter((c) => !allValues.has(c.value));
                setAvailableColumns([...allColumnOptions, ...extraFromApi]);
            })
            .catch(() => {
                setAvailableColumns(allColumnOptions);
            });

        const rulesPromise = conditionalRulesService
            .fetchRules(abortController)
            .then((response) => {
                if (response.length > 0) {
                    setRuleUuid(response[0].ruleUuid);
                    const loadedRules = apiResponseToRules(response);
                    setRules(loadedRules);
                    setSavedRules(loadedRules);
                    nextIdRef.current = loadedRules.length + 1;
                }
            })
            .catch(() => {
                if (!abortController.signal.aborted) {
                    console.error("Failed to fetch conditional formatting rules.");
                }
            });

        Promise.all([columnsPromise, rulesPromise]).finally(() => setIsLoading(false));

        return () => abortController.abort();
    }, []);

    const addRule = () => {
        if (rules.length >= MAX_RULES) {
            return;
        }
        const id = nextIdRef.current++;
        setRules((prev) => [
            ...prev,
            {
                id,
                enabled: true,
                columnName: "",
                condition: CONDITION_OPTIONS[0].translationKey,
                value: "",
                color: COLOR_OPTIONS[0].value,
                isEditing: true,
            },
        ]);
    };

    const updateRule = (id: number, field: keyof RuleRow, val: string | boolean) => {
        setRules((prev) => prev.map((r) => (r.id === id ? { ...r, [field]: val } : r)));
    };

    const confirmRule = (id: number) => {
        const rule = rules.find((r) => r.id === id);
        if (!rule || !rule.columnName.trim() || !rule.value.trim()) {
            return;
        }
        setRules((prev) => prev.map((r) => (r.id === id ? { ...r, isEditing: false } : r)));
    };

    const editRule = (id: number) => {
        const rule = rules.find((r) => r.id === id);
        if (rule) {
            editSnapshotRef.current[id] = { ...rule };
        }
        setRules((prev) => prev.map((r) => (r.id === id ? { ...r, isEditing: true } : r)));
    };

    const deleteRule = (id: number) => {
        delete editSnapshotRef.current[id];
        setRules((prev) => prev.filter((r) => r.id !== id));
    };

    const cancelEdit = (id: number) => {
        const snapshot = editSnapshotRef.current[id];
        if (snapshot) {
            delete editSnapshotRef.current[id];
            setRules((prev) => prev.map((r) => (r.id === id ? { ...snapshot, isEditing: false } : r)));
        } else {
            deleteRule(id);
        }
    };

    const buildRuleCells = (rule: RuleRow, index: number): Array<string | React.JSX.Element> => {
        const selectedColor = COLOR_OPTIONS.find((c) => c.value === rule.color);
        const selectedColorLabel = selectedColor ? t(selectedColor.translationKey) : rule.color;
        const rowNumber = index + 1;

        const rowClass = draggedIndex === index ? style.draggedRow : rule.isEditing ? style.ruleRowEditing : undefined;

        const dragProps = !rule.isEditing
            ? {
                  draggable: true,
                  onDragStart: (e: React.DragEvent) => {
                      const tableRow = (e.currentTarget as HTMLElement).closest("tr");
                      if (tableRow) {
                          const rect = tableRow.getBoundingClientRect();
                          e.dataTransfer.setDragImage(tableRow, e.clientX - rect.left, e.clientY - rect.top);
                      }
                      setDraggedIndex(index);
                  },
                  onDragOver: (e: React.DragEvent) => handleDragOver(index, e),
                  onDrop: resetDragState,
                  onDragEnd: resetDragState,
              }
            : {};

        const td = (key: string, children: React.ReactNode) => (
            <td key={key} className={rowClass} {...dragProps}>
                {children}
            </td>
        );

        const numberCell = (
            <Box
                verticalAlignment="Center"
                gap="Lg"
                className={style.ruleCellNumber}
                testId={testIds.workArea.conditionalRules.table.ruleRow.itself}
            >
                <IconButton>
                    <Drag color={theme.iconFillColor} />
                </IconButton>
                <Box className={style.rowNumber}>{`${rowNumber}.`}</Box>
            </Box>
        );

        const enabledCell = (
            <Checkbox
                name={`rule-enabled-${rule.id}`}
                checked={rule.enabled}
                hideLabelSection={true}
                onChange={(e) => updateRule(rule.id, "enabled", e.target.checked)}
                disabled={isSaving}
                testIds={{ input: testIds.workArea.conditionalRules.table.ruleRow.enabledCheckbox }}
            />
        );

        if (!rule.isEditing) {
            return [
                td(`num-${rule.id}`, numberCell),
                td(`enabled-${rule.id}`, enabledCell),
                td(
                    `col-${rule.id}`,
                    <Box verticalAlignment="Center" gap="Xxs">
                        <Box className={style.ifThenLabel}>{t("ConditionalFormattingRules.addRuleForm.if")}</Box>
                        <span className={style.truncatedItem}>
                            {availableColumns.find((col) => col.value === rule.columnName)?.label ?? rule.columnName}
                        </span>
                    </Box>
                ),
                td(`cond-${rule.id}`, t(rule.condition)),
                td(`val-${rule.id}`, <span className={style.truncatedItem}>{rule.value}</span>),
                td(
                    `color-${rule.id}`,
                    <Box gap="Xxs" verticalAlignment="Center">
                        <Box className={style.ifThenLabel}>{t("ConditionalFormattingRules.addRuleForm.then")}</Box>
                        <Box className={style.colorSelectionPreText}>
                            {t("ConditionalFormattingRules.addRuleForm.colorSelectionPreText")}
                        </Box>
                        <Box
                            className={style.colorBadge}
                            style={{ backgroundColor: getColorHexFromTheme(rule.color, theme) }}
                        >
                            {selectedColorLabel}
                        </Box>
                    </Box>
                ),
                td(
                    `actions-${rule.id}`,
                    <Box horizontalAlignment="Center" verticalAlignment="Center" gap="Xs">
                        <IconButton
                            onClick={() => editRule(rule.id)}
                            disabled={isSaving || !hasManageRulesAuthority}
                            testId={testIds.workArea.conditionalRules.table.ruleRow.editButton}
                        >
                            <Edit color={theme.iconFillColor} />
                        </IconButton>
                        <IconButton
                            deleteButton
                            onClick={() => deleteRule(rule.id)}
                            disabled={isSaving || !hasManageRulesAuthority}
                            testId={testIds.workArea.conditionalRules.table.ruleRow.deleteButton}
                        >
                            <Delete color={theme.errorIconColor} />
                        </IconButton>
                    </Box>
                ),
            ];
        }

        return [
            td(`num-${rule.id}`, numberCell),
            td(`enabled-${rule.id}`, enabledCell),
            td(
                `col-${rule.id}`,
                <Box gap="Xxs" verticalAlignment="Center">
                    <Box className={style.ifThenLabel}>{t("ConditionalFormattingRules.addRuleForm.if")}</Box>
                    <SearchSelect
                        name={`rule-columnName-${rule.id}`}
                        placeholder={t("ConditionalFormattingRules.addRuleForm.columnName")}
                        items={getAvailableColumnsForRule(rule.columnName)}
                        selectedByDefault={rule.columnName}
                        onSelection={(val) => updateRule(rule.id, "columnName", String(val))}
                        disabled={isSaving}
                        hideLabelSection={true}
                        closeOnSelection={true}
                        dropdownMaxHeight={250}
                        testIds={{ input: testIds.workArea.conditionalRules.table.ruleRow.columnNameSelect }}
                        width={160}
                        containerRef={tableContainerRef}
                        flipOptions={{
                            fallbackPlacements: ["top-start"],
                        }}
                    />
                </Box>
            ),
            td(
                `cond-${rule.id}`,
                <Select
                    name={`rule-condition-${rule.id}`}
                    placeholder={t("ConditionalFormattingRules.addRuleForm.condition")}
                    items={CONDITION_OPTIONS.map((opt) => ({
                        value: opt.translationKey,
                        label: t(opt.translationKey),
                    }))}
                    selectedByDefault={rule.condition}
                    onSelection={(val) => updateRule(rule.id, "condition", String(val))}
                    disabled={isSaving}
                    hideLabelSection={true}
                    closeOnSelection={true}
                    testIds={{ input: testIds.workArea.conditionalRules.table.ruleRow.conditionSelect }}
                    width={160}
                    containerRef={tableContainerRef}
                    flipOptions={{
                        fallbackPlacements: ["top-start"],
                    }}
                />
            ),
            td(
                `val-${rule.id}`,
                <TextInput
                    name={`rule-value-${rule.id}`}
                    placeholder={t("ConditionalFormattingRules.addRuleForm.value")}
                    value={rule.value}
                    onChange={(e) => updateRule(rule.id, "value", e.target.value)}
                    disabled={isSaving}
                    hideLabelSection={true}
                    testIds={{ input: testIds.workArea.conditionalRules.table.ruleRow.valueInput }}
                    width={125}
                />
            ),
            td(
                `color-${rule.id}`,
                <Box gap="Xxs" verticalAlignment="Center">
                    <Box className={style.ifThenLabel}>{t("ConditionalFormattingRules.addRuleForm.then")}</Box>
                    <Box className={style.colorSelectionPreText}>
                        {t("ConditionalFormattingRules.addRuleForm.colorSelectionPreText")}
                    </Box>
                    <Select
                        name={`rule-color-${rule.id}`}
                        placeholder={t("ConditionalFormattingRules.addRuleForm.colorSelectionPreText")}
                        items={COLOR_OPTIONS.map((opt) => ({ value: opt.value, label: t(opt.translationKey) }))}
                        selectedByDefault={rule.color}
                        onSelection={(val) => updateRule(rule.id, "color", String(val))}
                        disabled={isSaving}
                        hideLabelSection={true}
                        closeOnSelection={true}
                        dropdownMaxHeight={200}
                        testIds={{ input: testIds.workArea.conditionalRules.table.ruleRow.colorSelect }}
                        containerRef={tableContainerRef}
                        flipOptions={{
                            fallbackPlacements: ["top-start"],
                        }}
                    />
                </Box>
            ),
            td(
                `actions-${rule.id}`,
                <Box horizontalAlignment="Center" verticalAlignment="Center" gap="Xs">
                    <IconButton
                        className={style.confirmIconButton}
                        onClick={() => confirmRule(rule.id)}
                        disabled={isSaving || !rule.columnName.trim() || !rule.value.trim()}
                        testId={testIds.workArea.conditionalRules.table.ruleRow.confirmButton}
                    >
                        <Checkmark color={resolveColor("--color-white")} size={14} />
                    </IconButton>
                    <IconButton
                        className={style.cancelIconButton}
                        onClick={() => cancelEdit(rule.id)}
                        disabled={isSaving}
                        testId={testIds.workArea.conditionalRules.table.ruleRow.deleteButton}
                    >
                        <Close color={theme.iconFillColor} size={14} />
                    </IconButton>
                </Box>
            ),
        ];
    };

    if (isLoading) {
        return <SubPageLayout className={style.rulesSubPageLayout} title={""} loading />;
    }

    const table = (
        <StaticTable
            tableRef={tableContainerRef}
            headers={[
                { value: "" },
                { value: t("ConditionalFormattingRules.addRuleForm.enableRule") },
                { value: t("ConditionalFormattingRules.addRuleForm.columnName") },
                { value: t("ConditionalFormattingRules.addRuleForm.condition") },
                { value: t("ConditionalFormattingRules.addRuleForm.value") },
                { value: t("ConditionalFormattingRules.addRuleForm.display") },
                { value: "" },
            ]}
            styledCells={rules.map((rule, index) => buildRuleCells(rule, index))}
        ></StaticTable>
    );

    const ruleFormView = (
        <Box columnLayout gap="Sm" className={style.rulesContainer}>
            <p className={style.subtitle}>{t("ConditionalFormattingRules.ruleViewSubTitle")}</p>
            <div>
                {rules.length > 0 && <div className={style.tableContainer}>{table}</div>}
                {hasManageRulesAuthority && rules.length < MAX_RULES && (
                    <Box marginTop="Sm" marginBottom="Sm">
                        <Button
                            variant="Tertiary"
                            icon={<Add color={theme.iconFillColor} />}
                            onClick={addRule}
                            disabled={isSaving}
                            testId={testIds.workArea.conditionalRules.addRuleButton}
                        >
                            {t("ConditionalFormattingRules.addRule")}
                        </Button>
                    </Box>
                )}
            </div>
        </Box>
    );

    return (
        <>
            <SubPageLayout
                className={style.rulesSubPageLayout}
                title={""}
                buttons={
                    hasManageRulesAuthority && (
                        <>
                            <Button
                                variant="Primary"
                                onClick={handleSave}
                                disabled={!hasChanges || isSaving}
                                isLoading={isSaving}
                                testId={testIds.workArea.conditionalRules.saveButton}
                            >
                                {t("Common.save")}
                            </Button>
                            <Button
                                variant="Secondary"
                                onClick={handleCancel}
                                disabled={!hasChanges}
                                testId={testIds.workArea.conditionalRules.cancelButton}
                            >
                                {t("Common.cancel")}
                            </Button>
                        </>
                    )
                }
            >
                {ruleFormView}
            </SubPageLayout>
            <Modal
                isOpen={isCancelDialogVisible}
                hideModal={() => setIsCancelDialogVisible(false)}
                title={t("ConditionalFormattingRules.cancel.title")}
                className={style.cancelDialog}
                buttons={
                    <>
                        <Button
                            variant="Primary"
                            onClick={() => setIsCancelDialogVisible(false)}
                            testId={testIds.common.dialog.closeButton}
                        >
                            {t("ConditionalFormattingRules.cancel.keepEditing")}
                        </Button>
                        <Button
                            variant="Secondary"
                            onClick={handleDontSave}
                            testId={testIds.common.confirmationDialog.confirmButton}
                        >
                            {t("ConditionalFormattingRules.cancel.dontSave")}
                        </Button>
                    </>
                }
            >
                <p>{t("ConditionalFormattingRules.cancel.warning")}</p>
            </Modal>
        </>
    );
}

function ConditionalFormattingRulesView() {
    const { t } = useTranslation();
    return (
        <ApplicationLayout
            mainRoute={RULES_ROUTE}
            viewTitle={t("ConditionalFormattingRules.ruleViewTitle")}
            view={<ConditionalFormattingRulesContent />}
        />
    );
}

export default ConditionalFormattingRulesView;
